import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FormSection = ({ 
  step, 
  formData, 
  onChange, 
  errors 
}) => {
  const handleInputChange = (field, value) => {
    onChange(field, value);
  };

  const handleCheckboxChange = (field, option, checked) => {
    const currentValues = formData?.[field] || [];
    const newValues = checked
      ? [...currentValues, option]
      : currentValues?.filter(v => v !== option);
    onChange(field, newValues);
  };

  const renderField = (field) => {
    switch (field?.type) {
      case 'text': case'email': case'url': case'tel':
        return (
          <Input
            key={field?.id}
            type={field?.type}
            label={field?.label}
            description={field?.description}
            placeholder={field?.placeholder}
            value={formData?.[field?.id] || ''}
            onChange={(e) => handleInputChange(field?.id, e?.target?.value)}
            error={errors?.[field?.id]}
            required={field?.required}
            className="mb-4 md:mb-6"
          />
        );

      case 'select':
        return (
          <Select
            key={field?.id}
            label={field?.label}
            description={field?.description}
            placeholder={field?.placeholder}
            options={field?.options}
            value={formData?.[field?.id] || ''}
            onChange={(value) => handleInputChange(field?.id, value)}
            error={errors?.[field?.id]}
            required={field?.required}
            searchable={field?.options?.length > 10}
            className="mb-4 md:mb-6"
          />
        );

      case 'textarea':
        return (
          <div key={field?.id} className="mb-4 md:mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label}
              {field?.required && <span className="text-error ml-1">*</span>}
            </label>
            {field?.description && (
              <p className="text-sm text-muted-foreground mb-2">{field?.description}</p>
            )}
            <textarea
              placeholder={field?.placeholder}
              value={formData?.[field?.id] || ''}
              onChange={(e) => handleInputChange(field?.id, e?.target?.value)}
              rows={4}
              className={`
                w-full px-4 py-3 rounded-lg border bg-background text-foreground
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                transition-all duration-250 resize-none
                ${errors?.[field?.id] ? 'border-error' : 'border-input'}
              `}
            />
            {errors?.[field?.id] && (
              <p className="text-sm text-error mt-2">{errors?.[field?.id]}</p>
            )}
          </div>
        );

      case 'checkbox-group':
        return (
          <div key={field?.id} className="mb-4 md:mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              {field?.label}
              {field?.required && <span className="text-error ml-1">*</span>}
            </label>
            {field?.description && (
              <p className="text-sm text-muted-foreground mb-3">{field?.description}</p>
            )}
            <div className="space-y-3">
              {field?.options?.map((option) => (
                <Checkbox
                  key={option?.value}
                  label={option?.label}
                  description={option?.description}
                  checked={(formData?.[field?.id] || [])?.includes(option?.value)}
                  onChange={(e) => handleCheckboxChange(field?.id, option?.value, e?.target?.checked)}
                />
              ))}
            </div>
            {errors?.[field?.id] && (
              <p className="text-sm text-error mt-2">{errors?.[field?.id]}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-4 md:p-6 lg:p-8">
      <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name={step?.icon} size={20} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground font-heading mb-2">
            {step?.title}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {step?.description}
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        {step?.fields?.map(renderField)}
      </div>
      {step?.helpText && (
        <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              {step?.helpText}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSection;